import { DELTA, DIRS, type Dir, type DungeonIntent, MAP_H, MAP_W, type Pos } from '@xox/shared'
import { type PointerEvent, useEffect, useRef } from 'react'
import { cameraFor, VIEW_H, VIEW_W } from './camera'

const MOVE_KEYS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  s: 'down',
  a: 'left',
  d: 'right',
  W: 'up',
  S: 'down',
  A: 'left',
  D: 'right',
}
const SWIPE_MIN_PX = 24

/**
 * Keys (arrows/WASD move, Space or `.` wait, 1–5 use a pocket, Enter or `>` descend), swipes on
 * the board, and taps on a cell next to the hero. Everything becomes an intent for the queue.
 */
export function useDungeonInput(enabled: boolean, hero: Pos, enqueue: (i: DungeonIntent) => void) {
  const start = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      const dir = MOVE_KEYS[e.key]
      if (dir) {
        e.preventDefault()
        enqueue({ type: 'move', dir })
        return
      }
      if (e.key === ' ' || e.key === '.') {
        e.preventDefault()
        enqueue({ type: 'wait' })
      } else if (e.key === 'Enter' || e.key === '>') {
        enqueue({ type: 'descend' })
      } else if (/^[1-5]$/.test(e.key)) {
        enqueue({ type: 'use', slot: Number(e.key) - 1 })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, enqueue])

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    start.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const s = start.current
    start.current = null
    if (!s || !enabled) return
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) >= SWIPE_MIN_PX) {
      const dir: Dir =
        Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up'
      enqueue({ type: 'move', dir })
      return
    }
    // A tap: which cell, and is it next to the hero?
    const rect = e.currentTarget.getBoundingClientRect()
    const cam = cameraFor(hero)
    const c = cam.c + Math.floor(((e.clientX - rect.left) / rect.width) * VIEW_W)
    const r = cam.r + Math.floor(((e.clientY - rect.top) / rect.height) * VIEW_H)
    if (r < 0 || c < 0 || r >= MAP_H || c >= MAP_W) return
    if (r === hero.r && c === hero.c) {
      enqueue({ type: 'wait' })
      return
    }
    const dir = DIRS.find((d) => hero.r + DELTA[d].r === r && hero.c + DELTA[d].c === c)
    if (dir) enqueue({ type: 'move', dir })
  }
  const onPointerCancel = () => {
    start.current = null
  }

  return { onPointerDown, onPointerUp, onPointerCancel }
}
